/**
 * Sanitizes pasted HTML content for contentEditable editors.
 *
 * Only formatting supported by the editor toolbar is preserved:
 *   <b>, <strong>, <i>, <em>, <u>  – text formatting
 *   <a href target rel>            – hyperlinks (safe protocols only)
 *   <br>                           – line breaks
 *
 * Everything else (tables, images, scripts, inline styles, spans, …) is
 * stripped while keeping the text content.
 *
 * `keepLists` (board wave 01.11., L-29) — only the e-mail Paragraph, the one
 * text block with list buttons, asks for it: `<ul>` / `<ol>` / `<li>` stay too
 * (without attributes), their content is filtered as above, and the lists are
 * then normalized the way the editor makes them (`normalizeListsHtml`: one
 * level, a block inside an item as a line break, no empty items). Word's lists
 * are `MsoListParagraph` paragraphs, not `<ul>` — they still become lines.
 */
import { normalizeListsHtml } from "./list-html";

/** Tags that are allowed through, mapped to their permitted attributes. */
const ALLOWED_TAGS: Record<string, string[]> = {
  b: [],
  strong: [],
  i: [],
  em: [],
  u: [],
  br: [],
  a: ["href", "target", "rel"],
};

/** With `keepLists`: the list elements, without any attribute. */
const LIST_TAGS: Record<string, string[]> = {
  ...ALLOWED_TAGS,
  ul: [],
  ol: [],
  li: [],
};

export type PasteSanitizeOptions = {
  /** Keep `ul` / `ol` / `li` (the e-mail Paragraph only — see the file comment). */
  keepLists?: boolean;
};

/** Block-level elements: when stripped, a <br> is appended to preserve spacing. */
const BLOCK_ELEMENTS = new Set([
  "p",
  "div",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "li",
  "tr",
  "blockquote",
  "pre",
  "section",
  "article",
  "header",
  "footer",
  "table",
  "thead",
  "tbody",
  "tfoot",
  "ol",
  "ul",
  "dl",
  "dt",
  "dd",
]);

const ALLOWED_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"]);

/** Sanitized markup that ends with a kept list. */
const ENDS_IN_LIST = /<\/(ul|ol)>$/;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Parse and sanitize an HTML string, returning only allowed markup. */
export function sanitizePastedHtml(html: string, options: PasteSanitizeOptions = {}): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const allowed = options.keepLists ? LIST_TAGS : ALLOWED_TAGS;
  // Trim trailing <br> that block-element unwrapping may leave behind
  const result = sanitizeNode(doc.body, allowed).replace(/(<br\s*\/?>)+$/i, "");
  return options.keepLists ? normalizeListsHtml(result) : result;
}

/** Escape special HTML characters in plain text. */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function sanitizeNode(node: Node, allowed: Record<string, string[]>): string {
  // --- Text node ---
  if (node.nodeType === Node.TEXT_NODE) {
    return escapeHtml(node.textContent || "");
  }

  // --- Non-element, non-text (comments, etc.) ---
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return "";
  }

  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();
  // An item outside a list (half a list copied) is a line, not a list item.
  const orphanItem = tag === "li" && !el.parentElement?.closest("ul,ol");
  const allowedAttrs = orphanItem ? undefined : allowed[tag];

  // --- Allowed tag ---
  if (allowedAttrs !== undefined) {
    // Self-closing
    if (tag === "br") {
      return "<br>";
    }

    const attrs = buildAllowedAttributes(el, allowedAttrs, tag);
    const children = sanitizeChildren(el, allowed);

    // Drop <a> tags whose href was stripped (e.g. javascript: URLs) — a link
    // without href is a dead element that confuses users.
    if (tag === "a" && !attrs.includes("href=")) {
      return children;
    }

    return `<${tag}${attrs}>${children}</${tag}>`;
  }

  // --- Disallowed tag → unwrap (keep children) ---
  const children = sanitizeChildren(el, allowed);
  // Append <br> after block elements so paragraphs don't collapse — not after
  // a kept list (`<div><ul>…</ul></div>`): the list ends its line itself, a
  // <br> after it is an empty line (audit r2 E1). Without keepLists a list is
  // never kept, so that path is unchanged.
  if (BLOCK_ELEMENTS.has(tag) && children.length > 0 && !(allowed === LIST_TAGS && ENDS_IN_LIST.test(children))) {
    return children + "<br>";
  }
  return children;
}

function sanitizeChildren(el: Element, allowed: Record<string, string[]>): string {
  let result = "";
  for (const child of Array.from(el.childNodes)) {
    result += sanitizeNode(child, allowed);
  }
  return result;
}

function buildAllowedAttributes(
  el: HTMLElement,
  allowedAttrs: string[],
  tag: string
): string {
  if (allowedAttrs.length === 0) return "";

  let attrs = "";
  for (const attr of allowedAttrs) {
    const value = el.getAttribute(attr);
    if (value === null) continue;

    // Block dangerous URL schemes on <a href>
    if (tag === "a" && attr === "href" && !isAllowedUrl(value)) {
      continue;
    }

    attrs += ` ${attr}="${escapeAttr(value)}"`;
  }
  return attrs;
}

/**
 * PR-A4 / H9 — exported so non-paste callers (e.g. PortalPreview)
 * can guard hrefs that come from user-supplied config without
 * duplicating the allow-list. Same allow-list as the paste-sanitiser.
 */
export function isAllowedUrl(url: string): boolean {
  try {
    const parsed = new URL(url, "https://placeholder.com");
    return ALLOWED_PROTOCOLS.has(parsed.protocol);
  } catch {
    return false;
  }
}

/**
 * PR-A4 / H9 — convenience wrapper that returns the url itself if
 * safe, or `fallback` (default `"#"`) otherwise. The {@code "#"}
 * fallback renders an inert link rather than crashing, matching the
 * behaviour the legacy code already relied on when receiving null.
 */
export function safeHref(url: string | null | undefined, fallback: string = "#"): string {
  if (!url) return fallback;
  return isAllowedUrl(url) ? url : fallback;
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
